import { useIntl } from 'react-intl';
import { Tag } from 'sonata-react-components';

import { HoverTooltip } from '@/components/common/HoverTooltip/HoverTooltip';
import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import PoiLogo from './PoiLogo';

import type { DoohReportScreenPoi } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './poiBadge.scss';

type Props = {
  nearbyPoi: DoohReportScreenPoi | null;
  tagId: string;
};

/**
 * Brand logo + `Tag` with the brand of the screen's most affine POI, and its distance to the
 * screen underneath, on the "Nearby Affinity POI" column. A POI with no brand shows its label
 * (the address) instead.
 */
export default function PoiBadge({ nearbyPoi, tagId }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatCount, notAvailable } = useDoohReportFormatters();

  if (!nearbyPoi) {
    return <span className="poiBadge__empty">{notAvailable}</span>;
  }

  const name = nearbyPoi.brand ?? nearbyPoi.label;

  return (
    <span className="poiBadge">
      <PoiLogo alt={name} logoS3={nearbyPoi.logoS3} />
      <span className="poiBadge__details">
        <Tag
          className="poiBadge__tag"
          icon={{ position: 'left', webFont: 'fa-solid fa-location-dot' }}
          id={tagId}
          status="category"
        >
          <HoverTooltip content={name}>
            <span className="poiBadge__label">{name}</span>
          </HoverTooltip>
        </Tag>
        {nearbyPoi.distanceMeters !== null && (
          <span className="poiBadge__distance">
            {formatMessage(
              {
                defaultMessage: 'Distance to screen: {distance} m',
                id: 'doohReport.deliveryDrivers.column.poiDistance'
              },
              { distance: formatCount(nearbyPoi.distanceMeters) }
            )}
          </span>
        )}
      </span>
    </span>
  );
}
